import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { getLocalisedContent } from "@/lib/getLocalisedContent";
import { useChildren } from "./useChildren";
import { resolveActiveSpace } from "./useActiveSpace";

export interface CollectionTemplateStepRow {
  id: string;
  collection_template_id: string;
  order_index: number;
  required: boolean;
  step_title_override: string | null;
  card: { id: string; title: string | null } | null;
}

export interface CollectionTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
  is_prenatal: boolean;
  min_child_age_months: number | null;
  max_child_age_months: number | null;
  sort_order: number;
  requires_child: boolean;
  acorn_reward: number;
  /** 'guided' = predefined steps only; 'open' = free form; 'hybrid' = both */
  mode: 'guided' | 'open' | 'hybrid';
  /** true = user picks children at creation; locked permanently after */
  locks_to_children: boolean;
  /** true = creator locked automatically at collection creation time */
  locks_to_members: boolean;
  /** Keeper+ only. Seedling filters these out at render time; the browser
   *  still shows a locked teaser for users who don't have the entitlement. */
  premium: boolean;
  /** true = a separate collection may exist per child (e.g. Growing Up).
   *  false = single collection per user per template (e.g. Family Roots). */
  one_per_child: boolean;
  momentCount: number;
  translations?: Record<string, { title?: string | null; body?: string | null }> | null;
}

// Thin wrapper over the canonical resolveActiveSpace so collection creation
// lands in the exact same space the rest of the app is viewing. Previously
// this helper picked the user's owned space and silently fell back to
// any active membership — which could pick the wrong space for a Member
// in multiple families.
async function resolveSpaceId(userId: string): Promise<string> {
  const space = await resolveActiveSpace(userId);
  if (!space) {
    console.error("[useCollectionTemplates] resolveSpaceId: could not resolve active space", { userId });
    throw new Error("Unable to resolve your active space — please reload and try again.");
  }
  return space.id;
}

export function useCollectionTemplates() {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const { children, isLoading: childrenLoading } = useChildren();
  const childCount = children.length;

  const query = useQuery({
    queryKey: ["collection-templates", locale, childCount],
    enabled: !childrenLoading,
    queryFn: async () => {
      const hasChildren = childCount > 0;

      let queryBuilder = supabase
        .from("collection_templates")
        .select("*")
        .eq("active", true)
        .order("sort_order");

      if (!hasChildren) {
        queryBuilder = queryBuilder.eq("requires_child", false);
      }

      const { data } = await queryBuilder;
      if (!data) return [] as CollectionTemplate[];

      // Get step counts for each template
      const ids = data.map((t) => t.id);
      const { data: stepRows } = await supabase
        .from("collection_template_steps")
        .select("collection_template_id")
        .in("collection_template_id", ids);

      const counts: Record<string, number> = {};
      for (const row of stepRows ?? []) {
        counts[row.collection_template_id] = (counts[row.collection_template_id] ?? 0) + 1;
      }

      // Apply locale overlay: show translated name/description if available, otherwise
      // fall back to English. Templates are always shown — short titles are OK in English.
      const withCounts = data.map((t) => ({ ...t, momentCount: counts[t.id] ?? 0 }));
      const localised = withCounts
        .map((t) => getLocalisedContent(t as never, locale, true))
        .filter((t): t is typeof t & CollectionTemplate => t !== null);

      return localised as CollectionTemplate[];
    },
  });

  async function fetchTemplateSteps(templateId: string): Promise<CollectionTemplateStepRow[]> {
    const { data } = await supabase
      .from("collection_template_steps")
      .select("id, collection_template_id, order_index, required, step_title_override, card:content_library(id, title)")
      .eq("collection_template_id", templateId)
      .order("order_index");
    return (data ?? []) as unknown as CollectionTemplateStepRow[];
  }

  async function startCollectionFromTemplate(
    template: CollectionTemplate,
    userId: string,
    options?: {
      collectionName?: string;
      lockedChildIds?: string[];
      lockedMemberIds?: string[];
      participantChildIds?: string[];
      participantMemberIds?: string[];
      spaceId?: string;
    }
  ): Promise<string> {
    // Dedupe rule depends on template.one_per_child:
    //  - false → at most one collection per (user, template). Return the
    //    existing row if any.
    //  - true  → allow a separate collection per child. Only return an
    //    existing row when it already covers every child the caller is
    //    trying to create for (any overlap = would duplicate that child).
    //    Coverage is the union of locked_child_ids + participant_child_ids
    //    so both template-locked and free-form flows are respected.
    const { data: existingRows } = await supabase
      .from("collections")
      .select("id, locked_child_ids, participant_child_ids")
      .eq("user_id", userId)
      .eq("template_id", template.id);

    const existing = (existingRows ?? []) as Array<{
      id: string;
      locked_child_ids: string[] | null;
      participant_child_ids: string[] | null;
    }>;

    if (existing.length > 0) {
      if (!template.one_per_child) {
        return existing[0].id;
      }
      const targetChildIds = new Set([
        ...(options?.lockedChildIds ?? []),
        ...(options?.participantChildIds ?? []),
      ]);
      if (targetChildIds.size === 0) {
        // No child targeted (rare for per-child templates) — fall back to
        // single-instance behaviour so we don't create an orphan duplicate.
        return existing[0].id;
      }
      const match = existing.find((row) => {
        const covered = new Set([
          ...(row.locked_child_ids ?? []),
          ...(row.participant_child_ids ?? []),
        ]);
        for (const id of targetChildIds) {
          if (covered.has(id)) return true;
        }
        return false;
      });
      if (match) return match.id;
    }

    const spaceId = options?.spaceId ?? await resolveSpaceId(userId);

    const { data: collection, error } = await supabase
      .from("collections")
      .insert({
        user_id: userId,
        space_id: spaceId,
        template_id: template.id,
        name: options?.collectionName ?? template.name,
        icon: template.icon,
        color: template.color,
        mode: template.mode,
        locked_child_ids: options?.lockedChildIds ?? [],
        locked_member_ids: options?.lockedMemberIds ?? [],
        participant_child_ids: options?.participantChildIds ?? [],
        participant_member_ids: options?.participantMemberIds ?? [],
      })
      .select("id")
      .single();

    if (error) throw error;
    return (collection as { id: string }).id;
  }

  async function createCustomCollection(
    userId: string,
    params: {
      name: string;
      icon: string;
      color: string;
      participantChildIds: string[];
      participantMemberIds: string[];
    }
  ): Promise<string> {
    const spaceId = await resolveSpaceId(userId);

    const { data: collection, error } = await supabase
      .from("collections")
      .insert({
        user_id: userId,
        space_id: spaceId,
        name: params.name,
        icon: params.icon,
        color: params.color,
        template_id: null,
        mode: "open",
        locked_child_ids: [],
        locked_member_ids: [],
        participant_child_ids: params.participantChildIds,
        participant_member_ids: params.participantMemberIds,
      })
      .select("id")
      .single();

    if (error) throw error;
    return (collection as { id: string }).id;
  }

  return {
    data: query.data,
    templates: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    startCollectionFromTemplate,
    createCustomCollection,
    fetchTemplateSteps,
  };
}
